import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import config from '@config/index';
import { TMap } from '@shared/types';

type TWebsocketEvents = {
  onConnect?: (retry: boolean) => void;
  onConnectError?: () => void;
  onDisconnect?: (expected: boolean) => void;
  onUnmount: () => void;
};

type TState = 'NONE' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING';
const useWebsocket = (path: string, params: TMap<string, string>, events: TWebsocketEvents) => {
  const id = useMemo(() => uuidv4(), []);

  const [connected, setConnected] = useState(false);
  const [retried, setRetried] = useState(false);
  const refWs = useRef<Socket | null>(null);
  const refState = useRef<TState>('NONE');

  const connect = useCallback(() => {
    let fullUrl = `${config.BACKEND_URI}${path}?id=${id}&`;

    for (const paramKey in params) fullUrl += `${paramKey}=${encodeURIComponent(params[paramKey])}&`;

    console.log('Connecting to WebSocket:', fullUrl);
    const ws = io(fullUrl, {
      autoConnect: true,
      reconnection: true,
    });
    refWs.current = ws;

    ws.on('connect', () => {
      const retry = refState.current === 'RECONNECTING';
      console.log('WebSocket connected! Retry:', retry);

      setConnected(true);
      refState.current = 'CONNECTED';
      if (events?.onConnect) events.onConnect(retry);
    });

    ws.on('connect_error', (error) => {
      if (refState.current === 'NONE') {
        console.error('WebSocket failed to connect!', error); // TOOD: log
        ws.close();
        if (events?.onConnectError) events.onConnectError();
      } else {
        console.error('WebSocket Error:', error); // TOOD: log
      }
    });

    ws.on('disconnect', (reason, description) => {
      console.log('WebSocket disconnected.', reason, description);

      if (refState.current === 'CONNECTED') {
        console.error('Websocket disconnected unexpectedly!'); // TOOD: log
        refState.current = 'RECONNECTING';
        setRetried(true);
        if (events?.onDisconnect) events.onDisconnect(false);
      } else if (refState.current === 'RECONNECTING') {
        // no error:
        console.log('Websocket disconnected during reconnection.'); // TOOD: log
      } else {
        setRetried(false);
        if (events?.onDisconnect) events.onDisconnect(true);
      }

      setConnected(false);
    });

    ws.on('connect_error', (error) => {
      console.error('Connection error:', error);
    });

    ws.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Reconnection attempt #${attemptNumber}`);
    });

    ws.on('reconnect_error', (error) => {
      console.error('Reconnection error:', error);
    });

    ws.on('reconnect_failed', () => {
      console.error('Reconnection failed');
    });

    /*
    ws.onmessage = (event) => {
      console.log('Message from WebSocket:', event.data);
      setMessages(prevMessages => [...prevMessages, event.data]);
    };
    */
  }, [path, params, events]);

  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket...');
    if (refWs.current) {
      refState.current = 'NONE';
      refWs.current.close();
    }
  }, []);

  useEffect(() => {
    return () => {
      console.log('Cleaning up WebSocket...');
      events.onUnmount();
    };
  }, []);

  return {
    connected,
    retried,
    connect,
    disconnect,
  };
};

export { useWebsocket };
